import { Formik, Form, ErrorMessage, Field } from 'formik';
import * as Yup from 'yup';
import { useEffect, useState, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import AsyncSelect from 'react-select/async';
import showToast from '@/components/ui/Toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';
import { Button } from '@/components/shadcn/button';
import { Skeleton } from '@/components/shadcn/skeleton';
import { arenaService } from '@/services/arenaService';
import communityService from '@/services/communityService';
import { wilayahService, KotaOption } from '@/services/wilayahService';
import FileUploadField from '@/components/community/ProfileSettings/FileUploadField';

// Format string to URL slug: lowercase, only a-z 0-9, dash (-), and underscore (_)
// Tidak memotong - atau _ di awal/akhir agar user bisa mengetik dengan bebas
function formatSlug(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9_-]/g, '')
    .replace(/-+/g, '-')
    .replace(/_+/g, '_');
}

// Types
interface ProfileValues {
  namaCommunity: string;
  slug: string;
  kategoriCommunity: string;
  logoCommunity: File | null;
  coverBanner: File | null;
  lokasi: string;
  linkGroup: string;
  deskripsi: string;
  instagram: string;
  website: string;
  tiktok: string;
  youtube: string;
  gallery: File[];
  visibilitas: 'public' | 'private';
}

// Initial Values
const profileInitial: ProfileValues = {
  namaCommunity: '',
  slug: '',
  kategoriCommunity: '',
  logoCommunity: null,
  coverBanner: null,
  lokasi: '',
  linkGroup: '',
  deskripsi: '',
  instagram: '',
  website: '',
  tiktok: '',
  youtube: '',
  gallery: [],
  visibilitas: 'public',
};

// Validation Schema
const profileSchema = Yup.object({
  namaCommunity: Yup.string().required('Nama komunitas wajib diisi'),
  slug: Yup.string()
    .required('Slug wajib diisi')
    .min(3, 'Slug minimal 3 karakter')
    .matches(/^[a-z0-9_-]+$/, 'Slug hanya boleh huruf kecil, angka, strip (-), dan underscore (_)'),
  kategoriCommunity: Yup.string().required('Kategori komunitas wajib diisi'),
  lokasi: Yup.string().required('Lokasi wajib diisi'),
  linkGroup: Yup.string().url('Format URL tidak valid'),
  instagram: Yup.string().url('Format URL tidak valid'),
  website: Yup.string().url('Format URL tidak valid'),
  tiktok: Yup.string().url('Format URL tidak valid'),
  youtube: Yup.string().url('Format URL tidak valid'),
  gallery: Yup.array().max(10, 'Maksimal 10 foto'),
});

// Quill editor modules configuration
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
  ],
};

const quillFormats = ['bold', 'italic', 'underline', 'list', 'bullet'];

// Kota/Kabupaten search styles
const kotaSelectStyles = {
  control: (base: any) => ({
    ...base,
    borderColor: '#d1d5db',
    '&:hover': {
      borderColor: '#9ca3af',
    },
    boxShadow: 'none',
    fontSize: '0.875rem',
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: '0.875rem',
    backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
  }),
};

const ProfileTab = () => {
  const [initialValues, setInitialValues] = useState<ProfileValues>(profileInitial);
  const [sportCategories, setSportCategories] = useState<any[]>([]);
  const [selectedKota, setSelectedKota] = useState<KotaOption | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [existingAssets, setExistingAssets] = useState<{
    logo: string | null;
    cover: string | null;
    gallery: string[];
  }>({ logo: null, cover: null, gallery: [] });
  const [removedAssets, setRemovedAssets] = useState<{
    logo: boolean;
    cover: boolean;
    galleryIndexes: number[];
  }>({ logo: false, cover: false, galleryIndexes: [] });
  const mountedRef = useRef(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const slugDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [slugCheckStatus, setSlugCheckStatus] = useState<
    'idle' | 'checking' | 'available' | 'taken'
  >('idle');

  // Fetch sport categories
  const fetchSportCategories = useCallback(async () => {
    try {
      const categories = await arenaService.getSportCategories();
      if (!mountedRef.current) return;
      setSportCategories(categories);
    } catch (_error) {
      console.error('Failed to fetch sport categories:', _error);
    }
  }, []);

  // Fetch community profile data
  const fetchCommunityProfile = useCallback(async () => {
    setIsLoadingProfile(true);
    try {
      const response = await communityService.getProfile();
      if (!mountedRef.current) return;

      // Check if profile exists
      if (response?.data?.community) {
        const profile = response.data.community;

        // Extract social media links from nested structure
        const socialMedia = profile.links?.social_media || {};
        const linkGroup = profile.links?.link_group || '';

        // Set initial values from fetched profile
        setInitialValues({
          namaCommunity: profile.name || '',
          slug: profile.slug || formatSlug(profile.name || ''),
          kategoriCommunity: profile.sport_category || '',
          logoCommunity: null,
          coverBanner: null,
          lokasi: profile.location || '',
          linkGroup: linkGroup,
          deskripsi: profile.description || '',
          instagram: socialMedia.instagram || '',
          website: socialMedia.website || '',
          tiktok: socialMedia.tiktok || '',
          youtube: socialMedia.youtube || '',
          gallery: [],
          visibilitas: profile.is_public ? 'public' : 'private',
        });

        // Set selected kota if location exists
        if (profile.location) {
          setSelectedKota({
            value: profile.location,
            label: profile.location,
            nama: profile.location,
            namaLengkap: profile.location,
          });
        }

        // Extract existing assets
        if (profile.assets) {
          setExistingAssets({
            logo: profile.assets.logo || null,
            cover: profile.assets.cover || null,
            gallery: Array.isArray(profile.assets.gallery) ? profile.assets.gallery : [],
          });
        }
      }
    } catch (error: any) {
      // If 404 (no profile yet), it's okay - user is creating new profile
      if (error?.response?.status !== 404) {
        console.error('Failed to fetch community profile:', error);
      }
    } finally {
      if (mountedRef.current) {
        setIsLoadingProfile(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchSportCategories();
    fetchCommunityProfile();
    return () => {
      mountedRef.current = false;
      if (slugDebounceRef.current) clearTimeout(slugDebounceRef.current);
    };
  }, [fetchSportCategories, fetchCommunityProfile]);

  // Check slug availability with debounce (min 3 chars, 400ms delay)
  const checkSlug = useCallback((slug: string) => {
    if (slug.length < 3) {
      setSlugCheckStatus('idle');
      return;
    }
    if (slugDebounceRef.current) {
      clearTimeout(slugDebounceRef.current);
    }
    setSlugCheckStatus('checking');
    slugDebounceRef.current = setTimeout(async () => {
      if (!mountedRef.current) return;
      try {
        const result = await communityService.checkSlugAvailability(slug);
        if (!mountedRef.current) return;
        setSlugCheckStatus(result.available ? 'available' : 'taken');
      } catch (_e) {
        if (mountedRef.current) setSlugCheckStatus('idle');
      } finally {
        slugDebounceRef.current = null;
      }
    }, 400);
  }, []);

  // Run slug availability check when initial slug is set (e.g. after profile load)
  const initialSlugRef = useRef<string | null>(null);
  useEffect(() => {
    if (initialValues.slug.length >= 3 && initialSlugRef.current !== initialValues.slug) {
      initialSlugRef.current = initialValues.slug;
      checkSlug(initialValues.slug);
    }
  }, [initialValues.slug, checkSlug]);

  // Search Kota/Kabupaten with debounce
  const searchKota = useCallback(
    (inputValue: string, callback: (options: KotaOption[]) => void) => {
      // Clear previous timeout
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Don't search if less than 3 characters
      if (inputValue.length < 3) {
        callback([]);
        return;
      }

      // Debounce 500ms
      debounceRef.current = setTimeout(async () => {
        const options = await wilayahService.searchKota(inputValue);
        callback(options);
      }, 500);
    },
    []
  );

  // Validate image dimensions
  const validateImageDimensions = async (
    file: File,
    imageType: 'logo' | 'cover' | 'gallery'
  ): Promise<{ valid: boolean; error?: string }> => {
    return new Promise((resolve) => {
      // Check file size first (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        resolve({ valid: false, error: 'ukuran file maksimal 2MB' });
        return;
      }

      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        const width = img.width;
        const height = img.height;
        const ratio = width / height;

        switch (imageType) {
          case 'logo':
            // Logo must be square (1:1 ratio)
            if (width !== height) {
              resolve({ valid: false, error: 'logo harus berbentuk square (contoh: 512x512px)' });
              return;
            }
            // Recommended size: 256-2048px
            if (width > 2048) {
              resolve({
                valid: false,
                error: 'ukuran logo direkomendasikan harus kurang dari 2048x2048px',
              });
              return;
            }
            break;

          case 'cover':
            // Cover must be rectangle with aspect ratio around 4:1 (1200x300)
            if (ratio < 3.5 || ratio > 4.5) {
              resolve({
                valid: false,
                error: 'cover harus berbentuk persegi panjang (contoh: 1200x300px)',
              });
              return;
            }
            // Recommended size: 800-2400px width
            if (width < 800 || width > 2400) {
              resolve({
                valid: false,
                error: 'lebar cover direkomendasikan antara 800px hingga 2400px',
              });
              return;
            }
            break;

          case 'gallery':
            // Gallery accepts any ratio, only validate file size (already checked above)
            break;
        }

        resolve({ valid: true });
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({ valid: false, error: 'invalid image file' });
      };

      img.src = url;
    });
  };

  const handleSubmit = async (values: ProfileValues, { setSubmitting }: any) => {
    try {
      // Validate logo dimensions if uploaded
      if (values.logoCommunity) {
        const logoValidation = await validateImageDimensions(values.logoCommunity, 'logo');
        if (!logoValidation.valid) {
          showToast.error(logoValidation.error || 'Logo tidak valid');
          setSubmitting(false);
          return;
        }
      }

      // Validate cover dimensions if uploaded
      if (values.coverBanner) {
        const coverValidation = await validateImageDimensions(values.coverBanner, 'cover');
        if (!coverValidation.valid) {
          showToast.error(coverValidation.error || 'Cover tidak valid');
          setSubmitting(false);
          return;
        }
      }

      // Validate gallery images dimensions if uploaded
      if (values.gallery && values.gallery.length > 0) {
        for (let i = 0; i < values.gallery.length; i++) {
          const galleryValidation = await validateImageDimensions(values.gallery[i], 'gallery');
          if (!galleryValidation.valid) {
            showToast.error(`Foto gallery ke-${i + 1}: ${galleryValidation.error}`);
            setSubmitting(false);
            return;
          }
        }
      }

      // Build kept assets (assets that weren't removed)
      const keptAssets: any = {};

      // Keep logo if exists and not removed
      if (existingAssets.logo && !removedAssets.logo) {
        keptAssets.logo = existingAssets.logo;
      }

      // Keep cover if exists and not removed
      if (existingAssets.cover && !removedAssets.cover) {
        keptAssets.cover = existingAssets.cover;
      }

      // Keep gallery images that weren't removed
      if (existingAssets.gallery.length > 0) {
        keptAssets.gallery = existingAssets.gallery.filter(
          (_, index) => !removedAssets.galleryIndexes.includes(index)
        );
      }

      const payload = {
        name: values.namaCommunity,
        sport_category: values.kategoriCommunity,
        location: values.lokasi,
        slug: values.slug.trim() || undefined,
        description: values.deskripsi,
        link_group: values.linkGroup,
        instagram: values.instagram,
        website: values.website,
        tiktok: values.tiktok,
        youtube: values.youtube,
        is_public: values.visibilitas === 'public',
        logo: values.logoCommunity,
        cover: values.coverBanner,
        gallery: values.gallery,
        keep_assets: keptAssets,
      };

      await communityService.updateProfile(payload);
      showToast.success('Profile berhasil diperbarui!');
      // Reset removed assets state
      setRemovedAssets({ logo: false, cover: false, galleryIndexes: [] });
      // Refresh profile data after successful update
      fetchCommunityProfile();
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message || '';
      const isSlugConflict =
        error?.response?.status === 409 ||
        (typeof apiMessage === 'string' &&
          apiMessage.toLowerCase().includes('slug') &&
          apiMessage.includes('sudah digunakan'));
      const errorMessage = isSlugConflict
        ? 'Slug tersebut sudah digunakan oleh komunitas lain. Silakan pilih slug lain.'
        : apiMessage || 'Terjadi kegagalan ketika update data, silahkan hubungi administrator';
      showToast.error(errorMessage);
      if (!isSlugConflict) {
        console.error('Error updating profile:', error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoadingProfile) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={profileSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Informasi Dasar */}
              <Card>
                <CardHeader>
                  <CardTitle>Profile</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="namaCommunity">
                      Nama Komunitas<span className="text-destructive ml-1">*</span>
                    </Label>
                    <Field name="namaCommunity">
                      {({ field }: any) => (
                        <Input
                          id="namaCommunity"
                          placeholder="Masukkan nama komunitas"
                          {...field}
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="namaCommunity"
                      component="div"
                      className="text-destructive text-xs"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="slug">
                      Slug Komunitas (untuk URL)
                      <span className="text-destructive ml-1">*</span>
                    </Label>
                    <Field name="slug">
                      {({ field }: any) => (
                        <Input
                          id="slug"
                          placeholder="contoh: komunitas-sepak-bola"
                          {...field}
                          value={field.value}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const formatted = formatSlug(raw);
                            setFieldValue('slug', formatted);
                            checkSlug(formatted);
                          }}
                        />
                      )}
                    </Field>
                    {slugCheckStatus === 'checking' && (
                      <p className="text-xs text-muted-foreground">Memeriksa ketersediaan...</p>
                    )}
                    {slugCheckStatus === 'available' && values.slug.length >= 3 && (
                      <p className="text-xs text-green-600">Slug tersedia</p>
                    )}
                    {slugCheckStatus === 'taken' && (
                      <p className="text-xs text-destructive">
                        Slug sudah digunakan oleh komunitas lain
                      </p>
                    )}
                    <ErrorMessage
                      name="slug"
                      component="div"
                      className="text-destructive text-xs"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="kategoriCommunity">
                      Kategori Komunitas<span className="text-destructive ml-1">*</span>
                    </Label>
                    <Field name="kategoriCommunity">
                      {({ field }: any) => (
                        <Select
                          value={field.value === '' ? undefined : field.value}
                          onValueChange={(v: string) => setFieldValue('kategoriCommunity', v)}
                        >
                          <SelectTrigger id="kategoriCommunity">
                            <SelectValue placeholder="Pilih Kategori" />
                          </SelectTrigger>
                          <SelectContent>
                            {sportCategories
                              .filter((cat) => cat.slug)
                              .map((cat) => (
                                <SelectItem key={cat.slug!} value={cat.slug!}>
                                  {cat.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      )}
                    </Field>
                    <ErrorMessage
                      name="kategoriCommunity"
                      component="div"
                      className="text-destructive text-xs"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>
                      Lokasi (Kota)<span className="text-destructive ml-1">*</span>
                    </Label>
                    <AsyncSelect<KotaOption>
                      cacheOptions
                      loadOptions={searchKota}
                      value={selectedKota}
                      onChange={(option) => {
                        setSelectedKota(option);
                        setFieldValue('lokasi', option?.nama || '');
                      }}
                      placeholder="Ketik minimal 3 huruf untuk mencari..."
                      noOptionsMessage={({ inputValue }) =>
                        inputValue.length < 3
                          ? 'Ketik minimal 3 huruf'
                          : 'Tidak ada hasil ditemukan'
                      }
                      loadingMessage={() => 'Mencari...'}
                      isClearable
                      styles={kotaSelectStyles}
                      getOptionLabel={(option) => option.namaLengkap}
                      getOptionValue={(option) => option.value}
                    />
                    <ErrorMessage
                      name="lokasi"
                      component="div"
                      className="text-destructive text-xs"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="linkGroup">Link Group</Label>
                    <Field name="linkGroup">
                      {({ field }: any) => (
                        <Input id="linkGroup" placeholder="https://..." type="text" {...field} />
                      )}
                    </Field>
                  </div>

                  <div className="space-y-2">
                    <Label>Deskripsi</Label>
                    <ReactQuill
                      theme="snow"
                      value={values.deskripsi}
                      onChange={(value) => setFieldValue('deskripsi', value)}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Ceritakan tentang komunitas Anda..."
                      className="bg-background rounded-md border border-input"
                    />
                    <ErrorMessage
                      name="deskripsi"
                      component="div"
                      className="text-destructive text-xs"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="instagram">Instagram</Label>
                      <Field name="instagram">
                        {({ field }: any) => (
                          <Input
                            id="instagram"
                            placeholder="https://www.instagram.com/sporto.indo"
                            {...field}
                          />
                        )}
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website">Website</Label>
                      <Field name="website">
                        {({ field }: any) => (
                          <Input id="website" placeholder="https://sporto.id" {...field} />
                        )}
                      </Field>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="tiktok">TikTok</Label>
                      <Field name="tiktok">
                        {({ field }: any) => (
                          <Input id="tiktok" placeholder="https://tiktok.com/sporto" {...field} />
                        )}
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="youtube">YouTube</Label>
                      <Field name="youtube">
                        {({ field }: any) => (
                          <Input id="youtube" placeholder="https://youtube.com/sporto" {...field} />
                        )}
                      </Field>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Right Column - Gallery & Visibilitas */}
              <Card>
                <CardHeader>
                  <CardTitle>Assets</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FileUploadField
                    name="logoCommunity"
                    label="Logo Komunitas (Square max 2048x2048px)"
                    accept="image/*"
                    maxFiles={1}
                  />
                  {existingAssets.logo && !removedAssets.logo && (
                    <div className="relative inline-block group">
                      <img
                        src={existingAssets.logo}
                        alt="Current logo"
                        className="w-24 h-24 object-cover rounded-md border border-input"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => setRemovedAssets({ ...removedAssets, logo: true })}
                        aria-label="Hapus logo"
                      >
                        <Icon icon="mdi:close" className="w-4 h-4" />
                      </Button>
                    </div>
                  )}

                  <FileUploadField
                    name="coverBanner"
                    label="Cover/Banner (Max size: 1200x300px)"
                    accept="image/*"
                    maxFiles={1}
                  />
                  {existingAssets.cover && !removedAssets.cover && (
                    <div className="relative inline-block group max-w-md">
                      <img
                        src={existingAssets.cover}
                        alt="Current cover"
                        className="w-full h-24 object-cover rounded-md border border-input"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => setRemovedAssets({ ...removedAssets, cover: true })}
                        aria-label="Hapus cover"
                      >
                        <Icon icon="mdi:close" className="w-4 h-4" />
                      </Button>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>
                      Gallery Kegiatan
                      <span className="text-muted-foreground text-xs ml-2">
                        (Maks. 10 foto, tersisa:{' '}
                        {10 - (existingAssets.gallery.length - removedAssets.galleryIndexes.length)}
                        )
                      </span>
                    </Label>
                    <FileUploadField
                      name="gallery"
                      accept="image/*"
                      multiple
                      maxFiles={
                        10 - (existingAssets.gallery.length - removedAssets.galleryIndexes.length)
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Upload foto kegiatan komunitas untuk menarik lebih banyak anggota.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>
                      Visibilitas Komunitas<span className="text-destructive ml-1">*</span>
                    </Label>
                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        type="button"
                        variant={values.visibilitas === 'public' ? 'default' : 'outline'}
                        className="h-auto py-3 gap-2"
                        onClick={() => setFieldValue('visibilitas', 'public')}
                      >
                        {values.visibilitas === 'public' && (
                          <Icon icon="mdi:check-circle" className="w-5 h-5" />
                        )}
                        Public
                      </Button>
                      <Button
                        type="button"
                        variant={values.visibilitas === 'private' ? 'default' : 'outline'}
                        className="h-auto py-3 gap-2"
                        onClick={() => setFieldValue('visibilitas', 'private')}
                      >
                        {values.visibilitas === 'private' && (
                          <Icon icon="mdi:check-circle" className="w-5 h-5" />
                        )}
                        Private
                      </Button>
                    </div>
                    <ErrorMessage
                      name="visibilitas"
                      component="div"
                      className="text-destructive text-xs"
                    />
                    <p className="text-xs text-muted-foreground">
                      <strong>Public:</strong> Komunitas dapat dilihat oleh semua orang.
                      <br />
                      <strong>Private:</strong> Hanya anggota yang dapat melihat komunitas.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex items-center justify-end gap-3 pt-6 mt-6 border-t border-border">
              <Button type="button" variant="outline" onClick={() => window.history.back()}>
                Batal
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Menyimpan...' : 'Simpan Perubahan'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default ProfileTab;
